import ModalFlow from 'src/common/ModalFlow';
import { Client } from 'src/admin/users/types';
import { useClientsContext } from '../context/ClientsContext';
import FamilyDetailsModal from '../FamilyDetailsModal';
import { useClients } from '../hooks/useClients';
import { useFamily } from '../hooks/useFamily';
import { useUpdateFamily } from '../hooks/useUpdateFamily';
import AddContactsToFamilyModal from './AddContactsToFamily/AddContactsToFamilyModal';
import DefineRelationshipsModal from './DefineRelationshipsModal';
import {
  InProgressFamily,
  InProgressSecondaryMember,
  peerModelPayloadFromInProgressFamily,
  starModelPayloadFromInProgressFamily,
} from './dataHelpers';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useFlags } from 'src/feature-flags/FlagsHook';

export default function EditFamilyFlow() {
  const { flow, flowId, close } = useClientsContext();
  const { updateFamily } = useUpdateFamily();
  const { flagEnabledFamilyPeerModel } = useFlags();
  const initialData = useInProgressFamily(flowId);

  const opened = flow === 'edit-family';
  if (!opened) {
    return null;
  }

  const handleUpdateFamily = async (family: InProgressFamily) => {
    if (!family) {
      throw new Error('No family selected');
    }

    if (!flowId) {
      throw new Error('No family ID');
    }

    try {
      const hasFamilyDetails = !!family.name && !!family.advisorUserId;
      const hasMainMember = flagEnabledFamilyPeerModel || !!family.mainFamilyMember;
      if (!hasFamilyDetails || !hasMainMember) {
        throw new Error('Missing required family data');
      }

      const buildPayload = flagEnabledFamilyPeerModel
        ? peerModelPayloadFromInProgressFamily
        : starModelPayloadFromInProgressFamily;
      await updateFamily(flowId, buildPayload(family));
      close();
    } catch (error) {
      notifyAndLogError(
        'Failed to update family',
        'An error occurred while updating the family. Please try again.',
        error,
        { familyId: flowId, familyData: family },
      );
    }
  };

  return (
    <ModalFlow<InProgressFamily>
      steps={[FamilyDetailsModal, AddContactsToFamilyModal, DefineRelationshipsModal]}
      initialData={initialData}
      onComplete={handleUpdateFamily}
      onClose={close}
    />
  );
}

function useInProgressFamily(familyId?: string) {
  const { family } = useFamily(familyId);
  const { clients } = useClients();

  function getClient(clientId: string): Client | undefined {
    return clients.find(client => client.id === clientId);
  }

  const inProgressFamily: InProgressFamily | undefined = {
    name: family?.name,
    advisorUserId: family?.advisorUserId,
    mainFamilyMember: family?.mainFamilyMember
      ? getClient(family?.mainFamilyMember.clientId)
      : undefined,
    secondaryFamilyMembers: family?.secondaryFamilyMembers.reduce<InProgressSecondaryMember[]>(
      (acc, member) => {
        const client = getClient(member.clientId);
        if (client) {
          acc.push({
            ...client,
            relationshipToMainFamilyMember: member.relationshipToMainFamilyMember,
          });
        }
        return acc;
      },
      [],
    ),
  };

  return inProgressFamily;
}
